import pouchIcon from "../assets/icons/chrome/usepouch.png";

// What Chrome on the Mac and on the iPhone share: what the address bar shows and takes, and a tab's
// history of pages loaded live in a frame.

export const HOME = "https://usepouch.shop/";

// Sites the browser knows the title and icon of; others show their address and Google's icon.
const SITES = {
  "usepouch.shop": { title: "Use Pouch - Peer-to-Peer Delivery Marketplace", icon: pouchIcon },
};

// The address as Chrome's address bar shows it: no "https://", no "www.", no lone trailing "/".
export const displayUrl = (url) =>
  url
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/^([^/]*)\/$/, "$1");

export const hostOf = (url) => {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch (e) {
    return url;
  }
};

export const siteOf = (url) =>
  SITES[hostOf(url)] || { title: hostOf(url), icon: `https://www.google.com/s2/favicons?domain=${hostOf(url)}&sz=32` };

export const openTab = (url) => window.open(url, "_blank", "noopener");

// What's typed in the address bar: a web address loads in the tab; anything else is a Google
// search, which opens in a real tab, as Google can't be shown inside another page.
export const go = (text, navigate) => {
  const t = text.trim();
  if (!t) return;
  if (/^https?:\/\//i.test(t)) navigate(t);
  else if (!/\s/.test(t) && /^localhost(:\d+)?(\/|$)/i.test(t)) navigate(`http://${t}`);
  else if (!/\s/.test(t) && /\.[a-z]{2,}(\/|$|:)/i.test(t)) navigate(`https://${t}`);
  else openTab(`https://www.google.com/search?q=${encodeURIComponent(t)}`);
};

// Where a page in the frame is. A site can't be asked from outside (the browser keeps another
// site's page private), so a site that wants the address bar and Back/Forward to follow it reports
// its location: { type: "onlysly:location", kind, href, title }, where `kind` is "load", "push",
// "replace", "pop" or "title". The browser asks it to step through its own history with
// { type: "onlysly:navigate", action: "back" | "forward" }.

let lastId = 0;
// A tab: the pages it has been to (`history` of { url, load }, and `index`), what its frame loads
// (`src`, or null for the new tab page; a new `load` loads it afresh), the title its page reported,
// whether its page reports where it is (`reports`), and the step (-1 or 1) the page was asked to
// take (`asked`). Each page remembers the frame load it was seen in: the page itself can only step
// back and forward through the pages of its current load.
export const newTab = (url) => ({
  id: ++lastId,
  history: url ? [{ url, load: 1 }] : [],
  index: url ? 0 : -1,
  src: url,
  load: 1,
  title: null,
  reports: false,
  asked: 0,
});

export const urlOf = (t) => (t.index >= 0 ? t.history[t.index].url : null);

// The tab after loading `to` (a page, or null for the new tab page) afresh.
export const navigated = (t, to) => ({
  ...t,
  history: [...t.history.slice(0, t.index + 1), { url: to, load: t.load + 1 }],
  index: t.index + 1,
  src: to,
  load: t.load + 1,
  title: null,
  reports: false,
});

export const reloaded = (t) => {
  const history = [...t.history];
  history[t.index] = { ...history[t.index], load: t.load + 1 };
  return { ...t, history, src: history[t.index].url, load: t.load + 1, title: null, reports: false };
};

// Back (-1) or Forward (1): the page steps through its own history when it can (`frame` is the
// tab's frame), which it then reports; otherwise the tab loads that page afresh.
export const stepped = (t, delta, frame) => {
  const target = t.history[t.index + delta];
  if (!target) return t;
  if (t.reports && target.load === t.load && frame && frame.contentWindow) {
    frame.contentWindow.postMessage(
      { type: "onlysly:navigate", action: delta < 0 ? "back" : "forward" },
      new URL(t.src).origin,
    );
    return { ...t, asked: delta };
  }
  const history = [...t.history];
  history[t.index + delta] = { ...target, load: t.load + 1 };
  return { ...t, history, index: t.index + delta, src: target.url, load: t.load + 1, title: null, reports: false };
};

// Whether a message is the page in `frame` (which loaded `src`) reporting where it is.
export const isReportFrom = (e, frame, src) => {
  const { data } = e;
  if (!data || data.type !== "onlysly:location" || typeof data.href !== "string") return false;
  if (!frame || e.source !== frame.contentWindow || !src) return false;
  try {
    return e.origin === new URL(src).origin && new URL(data.href).origin === e.origin;
  } catch (err) {
    return false;
  }
};

// The tab after its page reports where it is.
export const reported = (t, { kind, href, title }) => {
  const entry = { url: href, load: t.load };
  const history = [...t.history];
  let { index } = t;
  const current = history[index];
  const replace = () => (history[index] = entry);
  const push = () => {
    history.splice(index + 1, Infinity, entry);
    index += 1;
  };
  const moved = (delta) => {
    index += delta;
    history[index] = entry;
  };
  if (t.asked && history[index + t.asked] && (kind === "pop" || kind === "load")) {
    // The step the browser asked for (a step across a full page load arrives as a load).
    moved(t.asked);
  } else if (kind === "pop" || (kind === "load" && t.reports && current && current.url !== href)) {
    // Back or Forward from inside the page: find which way it went.
    if (history[index - 1] && history[index - 1].url === href) moved(-1);
    else if (history[index + 1] && history[index + 1].url === href) moved(1);
    else if (kind === "load") push();
    else replace();
  } else if (kind === "replace" || (kind === "load" && !t.reports)) replace();
  else if (kind === "push" && (!current || current.url !== href)) push();
  return { ...t, history, index, title: title || t.title, reports: true, asked: 0 };
};
